import { ref } from 'vue';
import apiBaseUrl from './apiUrl';
import { allows, BROWSER_KEY, CONSENT_KEY, newChoice, readChoice, validChoice } from '@/services/cookieConsentCore.mjs';

let storage;
try { storage = window.localStorage; } catch { /* Browsers can disable persistent storage. */ }
export const cookieChoice = ref(readChoice(storage));
export const cookiePreferencesOpen = ref(false);
export const consentSaving = ref(false);
export const consentError = ref(false);
let syncPromise;
let memoryBrowserId;
function browserId() {
  try {
    let id = localStorage.getItem(BROWSER_KEY);
    if (!/^[0-9a-f-]{36}$/i.test(id || '')) { id = crypto.randomUUID(); localStorage.setItem(BROWSER_KEY, id); }
    return id;
  } catch { return memoryBrowserId || (memoryBrowserId = crypto.randomUUID()); }
}
function persist(choice) {
  try { localStorage.setItem(CONSENT_KEY, JSON.stringify(choice)); } catch { /* Fail closed if storage is unavailable. */ }
  cookieChoice.value = choice;
}
export function optionalAllowed(category) { return allows(cookieChoice.value, category); }
export function openCookiePreferences() { cookiePreferencesOpen.value = true; }

export async function syncCookieChoice() {
  if (syncPromise) return syncPromise;
  const choice = cookieChoice.value;
  if (!choice || choice.receipt_id === choice.choice_id) return true;
  consentSaving.value = true;
  consentError.value = false;
  syncPromise = (async () => {
    const controller = new AbortController();
    const timeout = window.setTimeout(() => controller.abort(), 12000);
    try {
      const base = apiBaseUrl.replace(/\/$/, '');
      let response;
      for (let attempt = 0; attempt < 2; attempt++) {
        const csrf = await fetch(`${base}/sanctum/csrf-cookie`, { credentials: 'include', signal: controller.signal });
        if (!csrf.ok) throw new Error('csrf');
        const token = document.cookie.split('; ').find(c => c.startsWith('XSRF-TOKEN='));
        const headers = { 'Content-Type': 'application/json', Accept: 'application/json' };
        if (token) headers['X-XSRF-TOKEN'] = decodeURIComponent(token.substring('XSRF-TOKEN='.length));
        response = await fetch(`${base}/api/privacy/cookie-consent`, {
          method: 'POST', credentials: 'include', headers, signal: controller.signal,
          body: JSON.stringify({ choice_id: choice.choice_id, browser_id: browserId(), policy_version: choice.policy_version,
            analytics: choice.analytics, support: choice.support, locale: choice.locale }),
        });
        // A late session cookie can invalidate the first CSRF handshake. Retry
        // only 419 once, with the same idempotent choice and total timeout.
        if (response.status !== 419) break;
      }
      const result = await response.json();
      if (!response.ok || result.data?.choice_id !== choice.choice_id) throw new Error('receipt');
      if (cookieChoice.value?.choice_id === choice.choice_id) persist({ ...choice, receipt_id: result.data.choice_id });
      return true;
    } catch { consentError.value = true; return false; }
    finally { window.clearTimeout(timeout); consentSaving.value = false; syncPromise = null; }
  })();
  return syncPromise;
}

export async function saveCookieChoice(categories) {
  if (consentSaving.value) return false;
  // Revocations take effect before waiting for a receipt; grants require its acknowledgement.
  persist(newChoice(categories, crypto.randomUUID()));
  const saved = await syncCookieChoice();
  if (saved) cookiePreferencesOpen.value = false;
  return saved;
}

window.addEventListener('storage', event => {
  if (event.key === CONSENT_KEY || event.key === null) cookieChoice.value = readChoice(storage);
});
window.setInterval(() => {
  if (cookieChoice.value && !validChoice(cookieChoice.value)) cookieChoice.value = null;
}, 60000);
